import { ReactNode, useState } from "react";
import { useController } from "react-hook-form";
import LinkPicker, {
	FORBIDDEN_SCHEME,
	LinkPickerValue,
	LinkType,
	looksLikeUrl
} from "src/components/LinkPicker/LinkPicker";

// Submit-time URL validation. Visual feedback inside LinkPicker is
// belt; this is braces — form submission is blocked if the URL is
// non-empty and either malformed or uses a forbidden scheme.
const validateUrl = (value: unknown): boolean | string => {
	if (value == null || value === "") return true;
	const trimmed = String(value).trim();
	if (!trimmed) return true;
	if (FORBIDDEN_SCHEME.test(trimmed)) return "This URL type is not allowed";
	if (!looksLikeUrl(trimmed)) return "Please enter a valid URL";
	return true;
};

interface BaseProps {
	/** Field name carrying the URL string. */
	urlName: string;
	label?: string;
	helperText?: string;
	/**
	 * When true, visually hides the picker's own label/helper text (kept
	 * for screen readers). Use this when the surrounding card already
	 * provides a visible title.
	 */
	hideLabel?: boolean;
	/**
	 * Disable specific link types — surfaces that don't yet support a
	 * given type (e.g. hashtag) can hide it.
	 */
	disabledTypes?: LinkType[];
	/**
	 * Optional field name carrying the hashtag UUID. Omit on surfaces
	 * that don't yet support hashtag (or use `disabledTypes={["hashtag"]}`).
	 * Required when `selectedFeedName` is set.
	 */
	feedName?: string;
	/**
	 * Transient field carrying the hashtag object (uuid + name) so the
	 * picker can render the chip without re-fetching the hashtag.
	 * Only meaningful when `feedName` is set; defaults to "selectedFeed".
	 */
	selectedFeedName?: string;
	/**
	 * Optional discriminator enum field ("WEB" | "FEED" | null) — used by
	 * surfaces (like the carousel) that explicitly store the link kind.
	 * When omitted, the active type is inferred from which field is set.
	 */
	linkTypeName?: string;
}

type WebviewFlagProps =
	| {
			/**
			 * Boolean field name where `true` means "open in system browser".
			 * Used by surfaces (like Carousel) that store this directly.
			 */
			openInBrowserName: string;
			webviewName?: never;
	  }
	| {
			/**
			 * Boolean field name where `true` means "open in in-app webview".
			 * The inverted convention used by KeyShouts and QuickLinks. The
			 * bridge transparently inverts so callers always think in terms
			 * of "external" vs "in-app".
			 */
			webviewName: string;
			openInBrowserName?: never;
	  }
	| {
			openInBrowserName?: never;
			webviewName?: never;
	  };

type Props = BaseProps & WebviewFlagProps;

// Each sub-controller only registers its own field — no phantom keys
// leak into the parent form's getValues / dirtyFields / submit payload.

interface UrlControllerProps {
	name: string;
	children: (
		value: string | null,
		onChange: (next: string | null) => void
	) => ReactNode;
}
const UrlController = ({ name, children }: UrlControllerProps) => {
	const { field } = useController({
		name,
		rules: { validate: validateUrl }
	});
	return <>{children(field.value ?? null, field.onChange)}</>;
};

interface FieldControllerProps<T> {
	name: string;
	defaultValue: T;
	children: (value: T, onChange: (next: T) => void) => ReactNode;
}
function FieldController<T>({
	name,
	defaultValue,
	children
}: FieldControllerProps<T>) {
	const { field } = useController({ name, defaultValue });
	return <>{children(field.value as T, field.onChange)}</>;
}

interface OptionalFieldControllerProps<T> {
	name: string | undefined;
	defaultValue: T;
	children: (value: T, onChange: ((next: T) => void) | null) => ReactNode;
	fallbackValue: T;
}
function OptionalFieldController<T>({
	name,
	defaultValue,
	fallbackValue,
	children
}: OptionalFieldControllerProps<T>) {
	if (!name) return <>{children(fallbackValue, null)}</>;
	return (
		<FieldController name={name} defaultValue={defaultValue}>
			{children}
		</FieldController>
	);
}

const LinkPickerStep = (props: Props) => {
	const {
		urlName,
		feedName,
		selectedFeedName = "selectedFeed",
		linkTypeName,
		label,
		helperText,
		hideLabel,
		disabledTypes
	} = props;

	// Surfaces that don't store an explicit linkType discriminator need a
	// local "selected type" so that picking a card with no URL typed yet
	// doesn't silently fall back to "none". Initialised lazily — first
	// render still infers from form values (so editing an existing record
	// shows the right card).
	const [localType, setLocalType] = useState<LinkType | null>(null);

	return (
		<UrlController name={urlName}>
			{(urlValue, urlOnChange) => (
				<OptionalFieldController<string | null>
					name={linkTypeName}
					defaultValue={null}
					fallbackValue={null}
				>
					{(linkTypeValue, linkTypeOnChange) => (
						<OptionalFieldController<string | null>
							name={feedName}
							defaultValue={null}
							fallbackValue={null}
						>
							{(feedValue, feedOnChange) => (
								<OptionalFieldController<{ uuid: string; name: string } | null>
									name={feedName ? selectedFeedName : undefined}
									defaultValue={null}
									fallbackValue={null}
								>
									{(selectedFeedValue, selectedFeedOnChange) => (
										<OptionalFieldController<boolean>
											name={
												"openInBrowserName" in props
													? props.openInBrowserName
													: undefined
											}
											defaultValue={false}
											fallbackValue={false}
										>
											{(openInBrowserValue, openInBrowserOnChange) => (
												<OptionalFieldController<boolean>
													name={
														"webviewName" in props
															? props.webviewName
															: undefined
													}
													defaultValue={true}
													fallbackValue={true}
												>
													{(webviewValue, webviewOnChange) => {
														const externalFlag =
															"openInBrowserName" in props &&
															props.openInBrowserName
																? Boolean(openInBrowserValue)
																: "webviewName" in props && props.webviewName
																	? !webviewValue
																	: false;

														const writeExternalFlag = (isExternal: boolean) => {
															if (openInBrowserOnChange)
																openInBrowserOnChange(isExternal);
															if (webviewOnChange) webviewOnChange(!isExternal);
														};

														// Active type — when the surface has a discriminator,
														// trust it. When it doesn't, prefer the user's local
														// choice; fall back to inference from form values so
														// existing records render their saved type on edit.
														const inferredType: LinkType = (() => {
															if (linkTypeName) {
																if (linkTypeValue === "WEB")
																	return externalFlag ? "external" : "webview";
																if (linkTypeValue === "FEED") return "hashtag";
																return "none";
															}
															if (selectedFeedValue) return "hashtag";
															if (urlValue)
																return externalFlag ? "external" : "webview";
															return "none";
														})();
														const activeType: LinkType = linkTypeName
															? inferredType
															: (localType ?? inferredType);

														const value: LinkPickerValue = (() => {
															if (
																activeType === "webview" ||
																activeType === "external"
															)
																return {
																	type: activeType,
																	url: urlValue ?? "",
																	hashtag: null
																};
															if (activeType === "hashtag")
																return {
																	type: "hashtag",
																	url: null,
																	hashtag: selectedFeedValue
																};
															return { type: "none", url: null, hashtag: null };
														})();

														const handleChange = (next: LinkPickerValue) => {
															setLocalType(next.type);
															if (next.type === "none") {
																if (linkTypeOnChange) linkTypeOnChange(null);
																urlOnChange(null);
																writeExternalFlag(false);
																if (feedOnChange) feedOnChange(null);
																if (selectedFeedOnChange)
																	selectedFeedOnChange(null);
																return;
															}
															if (
																next.type === "webview" ||
																next.type === "external"
															) {
																if (linkTypeOnChange) linkTypeOnChange("WEB");
																urlOnChange(next.url ?? null);
																writeExternalFlag(next.type === "external");
																if (feedOnChange) feedOnChange(null);
																if (selectedFeedOnChange)
																	selectedFeedOnChange(null);
																return;
															}
															if (next.type === "hashtag") {
																if (linkTypeOnChange) linkTypeOnChange("FEED");
																urlOnChange(null);
																writeExternalFlag(false);
																if (feedOnChange)
																	feedOnChange(next.hashtag?.uuid ?? null);
																if (selectedFeedOnChange)
																	selectedFeedOnChange(next.hashtag ?? null);
															}
														};

														return (
															<LinkPicker
																value={value}
																onChange={handleChange}
																label={label}
																helperText={helperText}
																hideLabel={hideLabel}
																disabledTypes={disabledTypes}
															/>
														);
													}}
												</OptionalFieldController>
											)}
										</OptionalFieldController>
									)}
								</OptionalFieldController>
							)}
						</OptionalFieldController>
					)}
				</OptionalFieldController>
			)}
		</UrlController>
	);
};

export default LinkPickerStep;
